import { lazy, Suspense } from "react";
import { defineQuiz, type Answers, type Question } from "@/schema/quizSchema";
import { deriveProfile } from "./manly/profile";
import { assetUrl } from "./manly/assets";
import { BuildingRows } from "./manly/BuildingRows";
// Eager (not code-split with the lazy funnel): the building screen shares these
// classes and renders BEFORE the funnel bundle loads. See QuizDefinition.styles.
import reportCss from "./manly/report.tokens.css?inline";

const ManlyFunnel = lazy(() => import("./manly/report"));

/** Q3 -> which villain bridge he sees. The bridge names his actual brand. */
function bridge1For(answers: Answers): string {
  const w = typeof answers["current-wash"] === "string" ? answers["current-wash"] : "";
  if (w === "natural") return "bridge1-natural";
  if (w === "whatever" || w === "noidea") return "bridge1-unknown";
  if (w === "oldspice" || w === "axe" || w === "dove") return `bridge1-${w}`;
  return "bridge1-unknown";
}

const BRIDGE1_HEADING = "It's not a hygiene problem. It's a chemistry problem.";

/** Adult-brand bridge, one static variant per brand so the villain is named. */
function adultBridge(id: string, brand: string): Question {
  return {
    id,
    type: "info",
    heading: BRIDGE1_HEADING,
    body: [
      `${brand} was made for grown men. Puberty just switched his glands on, and teen boys make about 3x more odor-causing bacteria than adult men. Adult formulas can't keep up.`,
      "The fragrance masks the smell for an hour. The SLS strips his skin raw. The bacteria survive both. And “fragrance” on a label can legally hide up to 3,000 undisclosed chemicals, including known hormone disruptors.",
      "The smell isn't coming back. It never left.",
    ],
  };
}

const QUESTIONS: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old is your son?",
    options: [
      { value: "9-11", label: "9–11" },
      { value: "12-14", label: "12–14" },
      { value: "15-17", label: "15–17" },
      { value: "18plus", label: "18+" },
    ],
  },
  {
    id: "goal",
    type: "single",
    prompt: "What would you love to change most for him?",
    options: [
      { value: "fresh", label: "He stays fresh all day: school, sport, whatever", emoji: "\u{1F550}" },
      { value: "clothes", label: "The smell stops living in his clothes", emoji: "\u{1F455}" },
      { value: "skin", label: "His skin clears up (back, chest, shoulders)", emoji: "✨" },
      { value: "shower", label: "He showers without a battle", emoji: "\u{1F6BF}" },
      { value: "peace", label: "Peace of mind about what's going on his skin", emoji: "\u{1F6E1}️" },
    ],
  },
  {
    id: "current-wash",
    type: "single",
    prompt: "What body wash is he using right now?",
    whyWeAsk:
      "Formulas fail in different ways. Knowing what he uses now tells us which failure pattern to check for.",
    options: [
      { value: "oldspice", label: "Old Spice" },
      { value: "axe", label: "Axe" },
      { value: "dove", label: "Dove Men" },
      { value: "natural", label: "A “natural” brand" },
      { value: "whatever", label: "Whatever's in the shower" },
      { value: "noidea", label: "Honestly, no idea" },
    ],
  },
  {
    id: "smell-returns",
    type: "single",
    prompt: "After he showers… how long before you can smell it again?",
    options: [
      { value: "hour", label: "Within the hour" },
      { value: "schoolday", label: "By the end of the school day" },
      { value: "physical", label: "The second he does anything physical" },
      { value: "never", label: "Honestly, it never fully goes away" },
      { value: "untracked", label: "I haven't tracked it" },
    ],
  },

  adultBridge("bridge1-oldspice", "Old Spice"),
  adultBridge("bridge1-axe", "Axe"),
  adultBridge("bridge1-dove", "Dove Men"),
  {
    id: "bridge1-natural",
    type: "info",
    heading: BRIDGE1_HEADING,
    body: [
      "You already suspected the mainstream stuff. Good instinct. But most naturals just mask odor with essential oils, so it's back by midday. And the sticks use baking soda, the stuff behind those painful rashes.",
      "Right idea. Wrong mechanism.",
    ],
  },
  {
    id: "bridge1-unknown",
    type: "info",
    heading: BRIDGE1_HEADING,
    body: [
      "Then check his label for one word: “fragrance.” It can legally hide up to 3,000 undisclosed chemicals, including known hormone disruptors.",
      "And all it does is mask the smell for an hour. The bacteria stay alive.",
    ],
  },

  {
    id: "where-it-shows",
    type: "multi",
    prompt: "Where does his B.O. show up?",
    helper: "Select all that apply.",
    min: 0,
    options: [
      { value: "bedroom", label: "His bedroom" },
      { value: "clothes", label: "School shirts and sports kit, even after washing" },
      { value: "car", label: "The car after practice" },
      { value: "walkpast", label: "You notice it when he walks past you" },
      { value: "skin", label: "His skin (breakouts on back, chest, shoulders)" },
    ],
  },
  {
    id: "activity",
    type: "single",
    prompt: "How active is he?",
    whyWeAsk:
      "Sweat itself is odorless. What matters is how much fuel it gives the bacteria already on his skin.",
    options: [
      { value: "daily", label: "Practice or sport most days" },
      { value: "few", label: "A few sessions a week" },
      { value: "weekend", label: "Weekend sport" },
      { value: "notmuch", label: "Not really into sport (yet)" },
    ],
  },
  {
    id: "beyond-smell",
    type: "multi",
    prompt: "Beyond the smell, what else is puberty throwing at him?",
    helper: "Select all that apply.",
    min: 0,
    options: [
      { value: "breakouts", label: "Breakouts on back, chest or shoulders" },
      { value: "dry", label: "Dry, irritated or sensitive skin" },
      { value: "rash", label: "Rash from deodorants (especially “natural” ones)" },
      { value: "moody", label: "Mood swings and a short fuse" },
      { value: "tired", label: "Always tired, low energy" },
      { value: "none", label: "None of these", exclusive: true },
    ],
  },
  {
    id: "bridge2",
    type: "info",
    heading: "It's not him. And it's not you.",
    body: [
      "You've been buying what the grocery store sells. Products made for grown men, tested on adult skin. Nobody told you teen chemistry needed something different.",
      "118,233+ boys have switched to the first hormone-safe body wash made for teen chemistry. Wild-harvested Kakadu Plum kills odor-causing bacteria at the source. Not masked. Gone.",
    ],
    testimonial: {
      quote: "With Manly, he doesn't stink after a full day at school and basketball practice. Total game changer.",
      author: "Jenn F.",
      stars: 5,
    },
  },
  {
    id: "objection",
    type: "single",
    prompt: "What's stopped you from fixing this before now?",
    options: [
      { value: "natural-failed", label: "Tried a natural product that stopped working by midday" },
      { value: "rash", label: "A natural deodorant gave him a rash" },
      { value: "smell", label: "He refuses anything that smells “weird”" },
      { value: "unaware", label: "I didn't know anything else existed" },
      { value: "ready", label: "Nothing. I'm ready to fix it" },
    ],
  },
];

/** Linear, except Q4 fans out to the villain bridge keyed on Q3. */
const NEXT: Record<string, string | null> = {
  age: "goal",
  goal: "current-wash",
  "current-wash": "smell-returns",
  "bridge1-oldspice": "where-it-shows",
  "bridge1-axe": "where-it-shows",
  "bridge1-dove": "where-it-shows",
  "bridge1-natural": "where-it-shows",
  "bridge1-unknown": "where-it-shows",
  "where-it-shows": "activity",
  activity: "beyond-smell",
  "beyond-smell": "bridge2",
  bridge2: "objection",
  objection: null,
};

export const manlyQuiz = defineQuiz({
  id: "manly-teen-bo-profile",
  slug: "manly-quiz",
  tenant: "manly",
  meta: {
    title: "The Teen B.O. Profile",
    introHeadline: "Why does your son still smell after showering?",
    introBody:
      "This 2-minute Odor Profile analyzes his age, activity, and what he's washing with, then shows you how to end it for good.",
    testimonial: {
      // Trimmed at the clause boundary, not reworded: the original continues
      // "...and the new deodorant", and the funnel no longer sells one. What
      // remains is Tracey's own wording about the wash.
      quote:
        "My 14 year old son had terrible BO. Straight away, after the first wash he smelled amazing!",
      author: "Tracey M., Verified Customer",
      avatar: assetUrl("testimonial-tracey.jpg"),
    },
    locale: "en",
    // 8 questions + 1 bridge-1 variant (of 5 mutually exclusive) + bridge2 = 10
    // screens on any single path. See manlyQuiz.test.tsx's self-checking test.
    progressTotal: 10,
  },
  questions: QUESTIONS,
  personas: [
    {
      key: "surface",
      label: "Surface",
      result: {
        tagline: "Masked and returning.",
        body: ["It keeps breaking back through because the bacteria survive the wash, and the pattern is setting in."],
      },
    },
    {
      key: "embedded",
      label: "Embedded",
      result: {
        tagline: "Established on his skin.",
        body: ["And, by now, living in the fabric."],
      },
    },
    {
      key: "compounding",
      label: "Compounding",
      result: {
        tagline: "The odor and the skin symptoms are feeding each other.",
        body: ["Both threads need to break at once."],
      },
    },
  ],

  branching: (answers, currentQuestionId) => {
    if (currentQuestionId === "smell-returns") return bridge1For(answers);
    return NEXT[currentQuestionId] ?? null;
  },

  classifier: (answers) => deriveProfile(answers).verdict.toLowerCase(),

  // Shared by the building loader AND the result funnel, so it lives at the
  // quiz level (all phases) rather than inside the lazy funnel.
  styles: reportCss,

  renderBuilding: ({ answers, onDone }) => <BuildingRows answers={answers} onDone={onDone} />,

  renderResult: ({ answers, trackCta }) => (
    <Suspense fallback={<div style={{ minHeight: 240 }} aria-busy="true" />}>
      <ManlyFunnel answers={answers} trackCta={trackCta} />
    </Suspense>
  ),
});
